import type { ReactNode } from 'react';

/**
 * PluginShell — standard wrapper for plugin UI pages.
 *
 * Provides consistent spacing, theme inheritance, and optional header.
 * Transparent for existing pages: if no `title` or `actions` are passed,
 * only the content wrapper is rendered (a simple grid container).
 *
 * Usage (minimal — transparent for existing pages):
 *   <PluginShell>
 *     <MyPage />
 *   </PluginShell>
 *
 * Usage (with header — for new pages):
 *   <PluginShell title="Menu" actions={<button>Nuovo</button>}>
 *     <MyPage />
 *   </PluginShell>
 */
export interface PluginShellProps {
  /** Optional page title. When provided, renders a header row above content. */
  title?: string;
  /** Optional actions rendered on the right side of the header. */
  actions?: ReactNode;
  /** Optional extra header content rendered below title/actions row. */
  headerExtra?: ReactNode;
  /** Page content. */
  children: ReactNode;
}

export function PluginShell({ title, actions, headerExtra, children }: PluginShellProps) {
  const hasHeader = Boolean(title || actions || headerExtra);

  if (!hasHeader) {
    return <div className="sk-plugin-shell">{children}</div>;
  }

  return (
    <div className="sk-plugin-shell">
      <header className="sk-plugin-shell__header">
        {title ? <h1 className="sk-plugin-shell__title">{title}</h1> : <span />}
        {actions ? <div className="sk-plugin-shell__actions">{actions}</div> : null}
      </header>
      {headerExtra ? <div className="sk-plugin-shell__header-extra">{headerExtra}</div> : null}
      {children}
    </div>
  );
}
